import { FormEvent, useCallback, useEffect, useState } from 'react';
import {
  deleteStockPart,
  fetchStockParts,
  refillStockPart,
  updateStockPart,
} from '../../api/manufacturingEquipment';
import {
  ManufacturingStockPart,
  ManufacturingStockPartPayload,
} from '../../types/manufacturingEquipment';
import './EquipTabPanel.css';

import { reportError } from '../../utils/notify';
import { formatNumberDisplay, formatNumberInput, parseNumberInput } from '../../utils/numberFormat';
function StockPartsTab() {
  const [rows, setRows] = useState<ManufacturingStockPart[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<ManufacturingStockPartPayload>({
    partName: '',
    spec: '',
    stockQty: 0,
    remark: '',
  });
  const [saving, setSaving] = useState(false);
  const [refillQty, setRefillQty] = useState<Record<number, string>>({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setRows(await fetchStockParts());
      setError('');
    } catch {
      reportError(setError, '보유 부속품을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openEdit = (row: ManufacturingStockPart) => {
    setEditingId(row.stockPartId);
    setForm({
      partName: row.partName,
      spec: row.spec ?? '',
      stockQty: row.stockQty,
      remark: row.remark ?? '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (editingId == null) return;
    if (!form.partName.trim()) {
      reportError(setError, '품명을 입력해 주세요.');
      return;
    }
    try {
      setSaving(true);
      await updateStockPart(editingId, {
        partName: form.partName.trim(),
        spec: form.spec?.trim() || undefined,
        stockQty: Number(form.stockQty) || 0,
        remark: form.remark?.trim() || undefined,
      });
      setShowForm(false);
      await load();
    } catch {
      reportError(setError, '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleRefill = async (row: ManufacturingStockPart) => {
    const addQty = parseNumberInput(refillQty[row.stockPartId] ?? '') ?? 0;
    if (addQty <= 0) {
      reportError(setError, '리필 수량을 입력해 주세요.');
      return;
    }
    try {
      await refillStockPart(row.stockPartId, addQty);
      setRefillQty((prev) => ({ ...prev, [row.stockPartId]: '' }));
      await load();
    } catch {
      reportError(setError, '리필에 실패했습니다.');
    }
  };

  const handleDelete = async (row: ManufacturingStockPart) => {
    if ((row.connectedEquipments?.length ?? 0) > 0) {
      reportError(setError, '주요 부속품으로 연결된 설비에서 먼저 제거해 주세요.');
      return;
    }
    if (!window.confirm(`'${row.partName}'을(를) 삭제할까요?`)) return;
    try {
      await deleteStockPart(row.stockPartId);
      await load();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '삭제에 실패했습니다.';
      reportError(setError, message);
    }
  };

  return (
    <div className="equip-tab-panel">
      <div className="equip-tab-panel__toolbar">
        <div>
          <h3>보유 부속품</h3>
          <p className="equip-tab-panel__hint">
            각 설비의 주요 부속품을 모아 본 재고입니다. 연결 설비와 보유 수량을 확인·리필합니다.
          </p>
        </div>
      </div>
      {error && <p className="equip-tab-panel__error">{error}</p>}
      {showForm && editingId != null && (
        <form className="equip-tab-panel__form" onSubmit={handleSubmit}>
          <label>
            품명 *
            <input
              value={form.partName}
              onChange={(e) => setForm((p) => ({ ...p, partName: e.target.value }))}
              required
            />
          </label>
          <label>
            사양
            <input
              value={form.spec ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, spec: e.target.value }))}
            />
          </label>
          <label>
            보유 수량
            <input
              type="text"
              inputMode="decimal"
              value={formatNumberDisplay(form.stockQty ?? 0)}
              onChange={(e) => {
                const parsed = parseNumberInput(formatNumberInput(e.target.value));
                setForm((p) => ({
                  ...p,
                  stockQty: parsed == null ? 0 : Math.max(0, parsed),
                }));
              }}
            />
          </label>
          <label>
            비고
            <input
              value={form.remark ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, remark: e.target.value }))}
            />
          </label>
          <div className="equip-tab-panel__form-actions">
            <button type="button" className="is-ghost" onClick={() => setShowForm(false)}>
              취소
            </button>
            <button type="submit" disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </button>
          </div>
        </form>
      )}
      {loading ? (
        <p className="equip-tab-panel__hint">불러오는 중...</p>
      ) : rows.length === 0 ? (
        <p className="equip-tab-panel__hint">
          아직 등록된 부속품이 없습니다. 설비 상세의 &apos;주요 부속품&apos;에서 먼저 등록하세요.
        </p>
      ) : (
        <table className="equip-tab-panel__table">
          <thead>
            <tr>
              <th>품명</th>
              <th>사양</th>
              <th style={{ width: '80px' }}>보유</th>
              <th>연결 제조설비</th>
              <th style={{ width: '160px' }}>리필</th>
              <th style={{ width: '120px' }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.stockPartId}>
                <td>{row.partName}</td>
                <td>{row.spec || '-'}</td>
                <td>{(row.stockQty ?? 0).toLocaleString('ko-KR')}</td>
                <td>
                  {(row.connectedEquipments?.length ?? 0) === 0
                    ? '-'
                    : row.connectedEquipments!
                        .map((eq) =>
                          eq.equipmentCode
                            ? `${eq.equipmentName}(${eq.equipmentCode})`
                            : eq.equipmentName
                        )
                        .join(', ')}
                </td>
                <td>
                  <div className="equip-tab-panel__row-actions">
                    <input
                      type="text"
                      inputMode="decimal"
                      style={{ width: 72, height: 28 }}
                      value={refillQty[row.stockPartId] ?? ''}
                      onChange={(e) =>
                        setRefillQty((prev) => ({
                          ...prev,
                          [row.stockPartId]: formatNumberInput(e.target.value),
                        }))
                      }
                      placeholder="수량"
                    />
                    <button type="button" className="is-ghost" onClick={() => void handleRefill(row)}>
                      리필
                    </button>
                  </div>
                </td>
                <td>
                  <div className="equip-tab-panel__row-actions">
                    <button type="button" className="is-ghost" onClick={() => openEdit(row)}>
                      수정
                    </button>
                    <button type="button" className="is-danger" onClick={() => void handleDelete(row)}>
                      삭제
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default StockPartsTab;
